import _ from 'lodash';
import moment from 'moment-timezone';
import semver from 'semver';

import { resolve } from './LinkMapper';
import config from '../config';
import appVersion from '../appVersion';

/**
 * Adapted from Modernizr
 */
let localStorageAvailable = (function supportsLocalStorage() {
    var test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
})();

/**
 * Decorator, if localStorage is not available, short-circuit and return null
 */
function checkLocalStorage(target, name, descriptor) {
    const original = descriptor.value;
    descriptor.value = function () {
        if (!localStorageAvailable) { return null; }
        return original.apply(this, arguments);
    };
    return descriptor;
}

function getVersionRange() {
    const major = semver.major(appVersion.versionWithoutHotfix);
    const minor = semver.minor(appVersion.versionWithoutHotfix);

    return `~${major}.${minor}`;
}

const LocalStorageHelper = {

    getNamespacedKey (key) {
        const namespace = config.get('namespace');
        return namespace ? `${namespace}-${key}` : key;
    },

    /**
     * The default check as to whether stored data is allowed to be used.
     * Returns true if valid
     * @param  {Object} storedData  Object containing `version`, `expires`, `data` keys
     * @return {Boolean}
     */
    isValidStoredVersion({ version }) {
        return semver.satisfies(version, getVersionRange());
    },

    @checkLocalStorage
    getExpiry(key) {
        const wrappedSource = localStorage.getItem(this.getNamespacedKey(key));

        if (!wrappedSource) {
            return null;
        }

        const wrapped = JSON.parse(wrappedSource);
        return wrapped.expires ? moment(wrapped.expires) : null;
    },

    /**
     * Wrap getItem, if we are on a different major or minor version
     * to the stored data, then the data is invalid, so return null
     * and also allow keys to expire
     */
    @checkLocalStorage
    getItem(key, validateStoredData = LocalStorageHelper.isValidStoredVersion) {
        try {
            const wrappedSource = localStorage.getItem(this.getNamespacedKey(key));

            if (!wrappedSource) {
                return null;
            }

            const wrapped = JSON.parse(wrappedSource);
            const expires = wrapped.expires ? moment(wrapped.expires) : false;

            //Return nothing if the key has expired
            if (expires && moment.isMoment(expires)) {
                const now = moment();
                if (expires.isBefore(now)) {
                    return null;
                }
            }

            //Return nothing if stored data is not valid (i.e. from an old version)
            if (!wrapped.version || !validateStoredData(wrapped)) {
                return null;
            }

            return wrapped.data;

        } catch (error) {
            console.error(error);
            return null;
        }
    },

    /**
     * Wrap the data and include the app version it was set with
     */
    @checkLocalStorage
    setItem(key, data, expires = false) {
        const wrapped = {
            version: appVersion.versionWithoutHotfix,
            data,
            expires: false
        };

        if (expires !== false) {
            wrapped.expires = moment().add(expires, 'seconds').format();
        }

        //Try to set the key, if it fails remove it as we could be out of storage
        //so clean up to avoid inconsistent state
        try {
            return localStorage.setItem(this.getNamespacedKey(key), JSON.stringify(wrapped));
        } catch (e) {
            console.error('LocalStorage error', e);
            return this.removeItem(this.getNamespacedKey(key));
        }
    },

    @checkLocalStorage
    objectFromLocalStorage(key, type, validateStoredData) {

        let value = this.getItem(key, validateStoredData);

        if (value) {
            return type ? resolve(value).as(type) : value;
        }

        return null;
    },

    @checkLocalStorage
    objectToLocalStorage(key, object, expires = false) {

        const value = _.result(object, 'toJS', object);

        return this.setItem(key, value, expires);
    },

    @checkLocalStorage
    removeItem(key) {
        return localStorage.removeItem(this.getNamespacedKey(key));
    },

    //Set whether localStorage is available, useful for tests
    forceAvailable (available = true) {
        localStorageAvailable = available;
    }
};

export default LocalStorageHelper;



// WEBPACK FOOTER //
// ./src/helpers/LocalStorage.js